// Shared HSEQ constants + small presentational components.
// Extracted from HSEQPage.jsx during Feb-2026 refactor.
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, ShieldCheck } from "lucide-react";

export const CLASS_COLOURS = {
  "Medical Treatment": "#00334C",
  "Lost Time Injury":  "#7C1D1D",
  "First Aid":         "#94A3B8",
  "Plant or Property": "#22C55E",
  "Near Miss":         "#FCEA00",
};

export const STATUS_COLOURS = {
  investigation:   "bg-sky-100 text-sky-800",
  action_pending:  "bg-amber-100 text-amber-800",
  open:            "bg-amber-100 text-amber-800",
  in_progress:     "bg-sky-100 text-sky-800",
  closed:          "bg-emerald-100 text-emerald-700",
  passed:          "bg-emerald-100 text-emerald-700",
  action_required: "bg-rose-100 text-rose-700",
  current:         "bg-emerald-100 text-emerald-700",
};

// AU financial year: FY27 = Jul-2026 → Jun-2027. Computed from today so new
// FYs appear automatically when July rolls around (was hardcoded ["FY26","FY25"]).
const _fyEndYear = (() => {
  const d = new Date();
  return d.getMonth() + 1 >= 7 ? d.getFullYear() + 1 : d.getFullYear();
})();
export const CURRENT_FY = `FY${_fyEndYear % 100}`;
export const FY_OPTIONS = Array.from(
  { length: Math.max(_fyEndYear - 2024, 2) },
  (_, i) => `FY${(_fyEndYear - i) % 100}`
);

export function KPI({ label, value, suffix, hint, tone = "default", testId }) {
  const tones = {
    default: "text-slate-900",
    danger:  "text-rose-700",
    warn:    "text-amber-700",
    good:    "text-emerald-700",
    brand:   "text-gbe",
  };
  return (
    <div data-testid={testId} className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 font-display text-2xl ${tones[tone]}`}>
        {value}
        {suffix && <span className="text-sm text-slate-500 ml-1">{suffix}</span>}
      </div>
      {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

export function SiteHeroTile({ testId, selected, onClick, icon: Icon, title, subtitle, statLine, accent }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`text-left rounded-xl border p-5 transition-all h-full ${
        selected
          ? "border-gbe shadow-lg shadow-gbe/10 ring-2 ring-gbe/20"
          : "border-slate-200 opacity-80 hover:opacity-100 hover:shadow"
      }`}
    >
      <div className={`bg-gradient-to-br ${accent} rounded-lg p-4 text-white h-full`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Icon className="w-5 h-5" />
              <h3 className="font-display text-lg font-semibold">{title}</h3>
            </div>
            <p className="text-xs text-white/80 mt-1">{subtitle}</p>
          </div>
          {selected && <Badge className="bg-white text-slate-900">Active</Badge>}
        </div>
        <div className="mt-3 text-sm text-white/90 font-mono">{statLine}</div>
      </div>
    </button>
  );
}

export function Field({ label, value, full, mono }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-sm text-slate-800 ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</div>
    </div>
  );
}

export function StatusChip({ label, active }) {
  return (
    <div className={`rounded-md border px-2 py-1 flex items-center gap-1.5 ${active ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-500"}`}>
      {active ? <CheckCircle2 className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
      <span>{label}</span>
    </div>
  );
}
